/**
 * [BL] Delivery estimate.
 *
 * Ported/adapted from bylumine_hydrogen (DeliveryTimesV2). Computes arrival dates
 * from configurable business-day windows (weekends excluded), based on the
 * shopper's current date. Region + window numbers are resolved server-side in
 * Liquid and passed as data-attributes; this element only does the date math and
 * formatting, so it always reflects "today" regardless of page cache.
 *
 * Data attributes (all business days):
 *   data-processing-min / data-processing-max   lead time added before shipping
 *   data-standard-min / data-standard-max       standard shipping window
 *   data-express-min / data-express-max         express shipping window
 *   data-mode = "single" | "range"
 *   data-standard-label / data-express-label     e.g. "Expected by" / "Fastest by"
 *   data-engraving-label                         e.g. "custom engraving" ("" to hide)
 *   data-engraving-override                      full custom note text (wins if set)
 *
 * Fills: [data-standard-date], [data-express-date], [data-engraving-note].
 */

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    // Re-render on connect and whenever the day could have rolled over.
    this.#render();
  }

  #num(name, fallback = 0) {
    const value = parseInt(this.getAttribute(name) ?? '', 10);
    return Number.isNaN(value) ? fallback : Math.max(0, value);
  }

  /** Adds `days` business days (Mon–Fri) to a date, returning a new Date. */
  #addBusinessDays(from, days) {
    const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const dow = date.getDay();
      if (dow !== 0 && dow !== 6) added += 1;
    }
    return date;
  }

  /** "Thu 20 Aug" */
  #format(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('weekday')} ${get('day')} ${get('month')}`;
  }

  #render() {
    const now = new Date();
    const procMin = this.#num('data-processing-min');
    const procMax = this.#num('data-processing-max');
    const mode = this.getAttribute('data-mode') === 'range' ? 'range' : 'single';

    const line = (methodMin, methodMax) => {
      const earliest = this.#addBusinessDays(now, procMin + methodMin);
      const latest = this.#addBusinessDays(now, procMax + methodMax);
      if (mode === 'range' && this.#format(earliest) !== this.#format(latest)) {
        return `${this.#format(earliest)} – ${this.#format(latest)}`;
      }
      // Single-date mode: both speeds headline their earliest date.
      return this.#format(earliest);
    };

    const standardEl = this.querySelector('[data-standard-date]');
    if (standardEl) {
      standardEl.textContent = line(
        this.#num('data-standard-min'),
        this.#num('data-standard-max')
      );
    }

    const expressEl = this.querySelector('[data-express-date]');
    if (expressEl) {
      expressEl.textContent = line(
        this.#num('data-express-min'),
        this.#num('data-express-max')
      );
    }

    const noteEl = this.querySelector('[data-engraving-note]');
    if (noteEl) {
      const override = this.getAttribute('data-engraving-override');
      const label = this.getAttribute('data-engraving-label');
      let note = '';
      if (override) {
        note = override;
      } else if (label && procMax > 0) {
        const range = procMin === procMax ? `${procMax}` : `${procMin}–${procMax}`;
        const unit = procMax === 1 ? 'day' : 'days';
        note = `Includes ${range} ${unit} for ${label}`;
      }
      noteEl.textContent = note;
      noteEl.hidden = note === '';
    }

    this.dataset.ready = 'true';
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
